/**
 * GovernanceWorkspaceShell — Operational layout container
 *
 * Dark dense operational workspace with synchronized panes.
 *
 * REFINEMENTS (V1.3):
 * - Added compact operational toolbar with workspace context
 * - Live/Replay/Compare segmented control
 * - Time range display
 * - System continuity status
 * - Toolbar visual hierarchy
 *
 * REFINEMENTS (V1.4):
 * - Added compact left-side operational navigation scaffold
 * - OBSERVE / ANALYZE / CONFIGURE / WORKSPACES sections
 * - System status surface
 * - Workspace identity context
 * - Active surface indication
 *
 * REFINEMENTS (V1.5):
 * - Added compact bottom tab bar for secondary surfaces
 * - EVENT LOG / RECEIPTS / DIFF VIEW / REPLAY COMPARISON tabs
 * - Bottom surface organization and replay cognition
 */

import React, { useState, useCallback } from 'react';
import type { OperationalWorkspaceState, OperationalViewMode } from '../modules/governanceEventStream/operationalRendering/types.js';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../modules/governanceEventStream/operationalVisualLanguage/index.js';
import { TimelinePane } from './panes/TimelinePane.js';
import { TraceExplorerPane } from './panes/TraceExplorerPane.js';
import { InspectorPane } from './panes/InspectorPane.js';
import { EventLogPane } from './panes/EventLogPane.js';
import { ReplayComparePane } from './panes/ReplayComparePane.js';
import { ReplayScrubberPane } from './panes/ReplayScrubberPane.js';
import { ImprovementPane } from './panes/ImprovementPane.js';
import { AuditSummaryPane } from './panes/AuditSummaryPane.js';
import { ContinuityTimelineSummaryPane } from './panes/ContinuityTimelineSummaryPane.js';
import { ReleaseStateMapPane } from './panes/ReleaseStateMapPane.js';
import { ProposalMemoryPane } from './panes/ProposalMemoryPane.js';
import { VerifiedSurfaceIndexPane } from './panes/VerifiedSurfaceIndexPane.js';
import { SurfaceFederationSummaryPane } from './panes/SurfaceFederationSummaryPane.js';
import { FederatedSurfaceIndexPane } from './panes/FederatedSurfaceIndexPane.js';
import { BoundaryCompositionSummaryPane } from './panes/BoundaryCompositionSummaryPane.js';
import { WorkspaceAdmissionPanel } from './panes/WorkspaceAdmissionPanel.js';
import { WorkspaceEvidencePane } from './panes/WorkspaceEvidencePane.js';
import { WorkspaceReceiptPane } from './panes/WorkspaceReceiptPane.js';
import { WorkspaceWorkUnitPane } from './panes/WorkspaceWorkUnitPane.js';
import { WorkspaceNextMovePane } from './panes/WorkspaceNextMovePane.js';
import { WorkspaceFormationGatePane } from './panes/WorkspaceFormationGatePane.js';
import { WorkspaceSystemicPatternsPane } from './panes/WorkspaceSystemicPatternsPane.js';
import { WorkspaceConstitutionPane } from './panes/WorkspaceConstitutionPane.js';

interface GovernanceWorkspaceShellProps {
  workspace: OperationalWorkspaceState;
  onSelectEvent: (eventId: string) => void;
  onToggleSpan: (spanId: string) => void;
  onSelectTransition: (transitionId: string) => void;
  onViewModeChange?: (viewMode: OperationalViewMode) => void;
  // ---- Pilot pane selection callbacks — WORKSPACE_SELECTION_EXTENSION_V1 ----
  /** Emitted when work-unit pane identity row clicked. Propagates selectedWorkUnitId. */
  onSelectWorkUnit?: (workUnitId: string) => void;
  /** Emitted when an evidence node row is clicked. Propagates selectedEvidenceNodeId. */
  onSelectEvidenceNode?: (nodeId: string) => void;
  /** Emitted when a receipt row is clicked. Propagates selectedReceiptId. */
  onSelectReceipt?: (receiptId: string) => void;
  /** Emitted when an admission entry work-unit ref is clicked. Propagates selectedAdmissionId. */
  onSelectAdmission?: (admissionId: string) => void;
  /** Emitted when a formation gate entry row is clicked. Propagates selectedFormationGateEntryId. */
  onSelectGateEntry?: (entryId: string) => void;
  /** Emitted when a systemic pattern entry row is clicked. Propagates selectedPatternEntryId. */
  onSelectPatternEntry?: (entryId: string) => void;
  /** Emitted when a next move entry row is clicked. Propagates selectedNextMoveEntryId. */
  onSelectNextMoveEntry?: (entryId: string) => void;
}

const TRANSITION_TAB_SWITCH = 'background-color 80ms ease-out, color 80ms ease-out';

type BottomTab =
  | 'event-log'
  | 'receipts'
  | 'diff-view'
  | 'replay-comparison'
  | 'improvement'
  | 'audit-summary'
  | 'continuity-timeline'
  | 'release-state-map'
  | 'proposal-memory'
  | 'verified-surfaces'
  | 'surface-federation'
  | 'federated-surfaces'
  | 'boundary-composition'
  | 'admission'
  | 'evidence'
  | 'receipt'
  | 'work-unit'
  | 'next-move'
  | 'formation-gate'
  | 'systemic-patterns'
  | 'constitution';

function formatCompactStreamId(streamId: string): string {
  if (!streamId) return '—';
  if (streamId.length <= 16) return streamId;
  return `${streamId.slice(0, 8)}…${streamId.slice(-8)}`;
}

function formatTimeRange(earliest: string | null, latest: string | null): string {
  if (!earliest || !latest) return '—';
  const e = earliest.slice(11, 16);
  const l = latest.slice(11, 16);
  return `${e} → ${l}`;
}

export const WorkspaceToolbar: React.FC<{
  workspace: OperationalWorkspaceState;
  onViewModeChange?: (viewMode: OperationalViewMode) => void;
}> = ({ workspace, onViewModeChange }) => {
  const continuityStatus = workspace.panes.continuity.currentStatus;
  const continuityColor = SEMANTIC[continuityStatus as keyof typeof SEMANTIC] || SEMANTIC.stable;
  const timeRange = formatTimeRange(
    workspace.panes.timeline.earliestTimestamp,
    workspace.panes.timeline.latestTimestamp
  );
  const isReplayMode = workspace.viewMode === 'replay';

  const modeMap: Record<string, OperationalViewMode> = {
    'LIVE': 'standard',
    'REPLAY': 'replay',
    'COMPARE': 'replay',
  };

  const currentModeLabel = workspace.viewMode === 'standard' ? 'LIVE' : 
                           workspace.viewMode === 'replay' ? 'REPLAY' : 
                           workspace.viewMode.toUpperCase();

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
      backgroundColor: isReplayMode ? SEMANTIC.replayWindow.bg : NEUTRAL.bg700,
      borderBottom: `1px solid ${NEUTRAL.border}`,
      height: 40,
      gap: SPACING.sp4,
      transition: 'background-color 120ms ease-out',
    }}>
      {/* Left: Workspace context */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACING.sp3,
        flex: 1,
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
        }}>
          <span style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: NEUTRAL.text400,
            letterSpacing: 0.3,
          }}>
            GOVERNANCE WORKSPACE
          </span>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text300,
            fontFamily: TYPOGRAPHY.mono.family,
          }}>
            {formatCompactStreamId(workspace.streamId)}
          </span>
        </div>
      </div>

      {/* Center: Continuity status (prominent) */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACING.sp3,
      }}>
        <span style={{
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
          fontWeight: 600,
          letterSpacing: 0.5,
        }}>
          CONTINUITY
        </span>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: SPACING.sp2,
        }}>
          <div style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: continuityColor.text,
            boxShadow: `0 0 8px ${continuityColor.text}40`,
          }} />
          <span style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: continuityColor.text,
            backgroundColor: continuityColor.chip,
            padding: '2px 8px',
            borderRadius: 3,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
            border: `1px solid ${continuityColor.border}`,
          }}>
            {continuityStatus}
          </span>
        </div>
      </div>

      {/* Right: Mode control and time range */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: SPACING.sp4,
      }}>
        {/* Mode control */}
        <div style={{
          display: 'flex',
          backgroundColor: NEUTRAL.bg600,
          borderRadius: 4,
          padding: 2,
          border: `1px solid ${NEUTRAL.border}`,
        }}>
          {['LIVE', 'REPLAY', 'COMPARE'].map((mode) => (
            <button
              key={mode}
              onClick={() => onViewModeChange?.(modeMap[mode] as OperationalViewMode)}
              style={{
                padding: '2px 8px',
                fontSize: TYPOGRAPHY.micro.size,
                fontWeight: 600,
                border: 'none',
                borderRadius: 2,
                backgroundColor: currentModeLabel === mode ? NEUTRAL.bg500 : 'transparent',
                color: currentModeLabel === mode ? NEUTRAL.text500 : NEUTRAL.text400,
                cursor: 'pointer',
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                transition: 'background-color 80ms ease-out',
              }}
              type="button"
            >
              {mode}
            </button>
          ))}
        </div>

        {/* Time range */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
        }}>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
          }}>
            TIME RANGE
          </span>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text300,
            fontFamily: TYPOGRAPHY.mono.family,
          }}>
            {timeRange}
          </span>
        </div>
      </div>
    </div>
  );
};

export const WorkspaceSidebar: React.FC<{
  workspace: OperationalWorkspaceState;
}> = ({ workspace }) => {
  const continuityStatus = workspace.panes.continuity.currentStatus;
  const continuityColor = SEMANTIC[continuityStatus as keyof typeof SEMANTIC] || SEMANTIC.stable;

  const renderSectionHeader = (label: string) => (
    <div style={{
      padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
      fontSize: TYPOGRAPHY.micro.size,
      fontWeight: 700,
      color: NEUTRAL.text400,
      textTransform: 'uppercase',
      letterSpacing: 0.5,
      marginTop: SPACING.sp3,
    }}>
      {label}
    </div>
  );

  const renderNavItem = (label: string, isActive: boolean) => (
    <div style={{
      padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
      fontSize: TYPOGRAPHY.caption.size,
      color: isActive ? NEUTRAL.text500 : NEUTRAL.text400,
      backgroundColor: isActive ? NEUTRAL.bg600 : 'transparent',
      cursor: 'default',
      transition: 'background-color 80ms ease-out',
    }}>
      {label}
    </div>
  );

  return (
    <div style={{
      width: 180,
      backgroundColor: NEUTRAL.bg700,
      borderRight: `1px solid ${NEUTRAL.border}`,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <div style={{ padding: SPACING.sp3, borderBottom: `1px solid ${NEUTRAL.border}` }}>
        <div style={{ fontSize: TYPOGRAPHY.label.size, fontWeight: 600, color: NEUTRAL.text500 }}>
          GOVERNANCE WORKSPACE
        </div>
        <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400, fontFamily: TYPOGRAPHY.mono.family, marginTop: SPACING.sp1 }}>
          {formatCompactStreamId(workspace.streamId)}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto' }}>
        {renderSectionHeader('OBSERVE')}
        {renderNavItem('Timeline', false)}
        {renderNavItem('Trace Explorer', false)}
        {renderNavItem('Event Log', false)}
        {renderNavItem('Continuity', false)}

        {renderSectionHeader('ANALYZE')}
        {renderNavItem('Inspector', false)}
        {renderNavItem('Graph', false)}
        {renderNavItem('Replay Compare', false)}
        {renderNavItem('Receipts', false)}

        {renderSectionHeader('CONFIGURE')}
        {renderNavItem('View Modes', false)}
        {renderNavItem('Replay Context', false)}
        {renderNavItem('Proof Filters', false)}

        {renderSectionHeader('WORKSPACES')}
        {renderNavItem('Current Workspace', true)}
        {renderNavItem('Adapter Context', false)}
        {renderNavItem('Workspace Identity', false)}
      </div>

      <div style={{ padding: SPACING.sp3, borderTop: `1px solid ${NEUTRAL.border}` }}>
        <div style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400, marginBottom: SPACING.sp2 }}>
          SYSTEM STATUS
        </div>
        <div style={{ fontSize: TYPOGRAPHY.micro.size, fontWeight: 600, color: SEMANTIC.stable.text, backgroundColor: SEMANTIC.stable.chip, padding: '2px 6px', borderRadius: 3, display: 'inline-block', textTransform: 'uppercase', letterSpacing: 0.3 }}>
          ALL SYSTEMS OPERATIONAL
        </div>
        <div style={{ fontSize: TYPOGRAPHY.micro.size, fontWeight: 600, color: continuityColor.text, backgroundColor: continuityColor.chip, padding: '2px 6px', borderRadius: 3, display: 'inline-block', textTransform: 'uppercase', letterSpacing: 0.3, marginTop: SPACING.sp2 }}>
          {continuityStatus}
        </div>
      </div>
    </div>
  );
};

export const DiffViewSurface: React.FC<{
  replayCompare: { windowId: string | null; windowStart: string | null; windowEnd: string | null; totalInWindow: number; totalOutWindow: number; replayDeterministic: boolean; disclaimer: string };
  continuity: { currentStatus: string };
  admissibility: { totalEntries: number; refusedCount: number; passedCount: number; unknownCount: number };
}> = ({ replayCompare, continuity, admissibility }) => {
  const formatTimestamp = (ts: string | null) => {
    if (!ts) return '—';
    return ts.slice(11, 19);
  };

  const renderComparisonRow = (field: string, before: string, after: string, delta: string) => (
    <div key={field} style={{
      display: 'grid',
      gridTemplateColumns: '120px 1fr 1fr 80px',
      gap: SPACING.sp2,
      padding: SPACING.sp2,
      borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
      fontSize: TYPOGRAPHY.micro.size,
    }}>
      <span style={{ fontWeight: 600, color: NEUTRAL.text500 }}>{field}</span>
      <span style={{ color: NEUTRAL.text400, fontFamily: TYPOGRAPHY.mono.family }}>{before}</span>
      <span style={{ color: NEUTRAL.text400, fontFamily: TYPOGRAPHY.mono.family }}>{after}</span>
      <span style={{
        fontWeight: 600,
        color: delta === '→' ? NEUTRAL.text400 : delta.includes('↓') ? SEMANTIC.refusal.text : SEMANTIC.stabilized.text,
        backgroundColor: delta === '→' ? 'transparent' : delta.includes('↓') ? SEMANTIC.refusal.chip : SEMANTIC.stabilized.chip,
        padding: '1px 4px',
        borderRadius: 2,
        textAlign: 'center',
        fontSize: TYPOGRAPHY.micro.size,
      }}>{delta}</span>
    </div>
  );

  return (
    <div style={{ padding: SPACING.sp3 }}>
      <div style={{
        fontSize: TYPOGRAPHY.label.size,
        fontWeight: 600,
        color: NEUTRAL.text500,
        marginBottom: SPACING.sp3,
      }}>
        REPLAY COMPARISON
      </div>

      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          padding: SPACING.sp2,
          borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
          fontWeight: 600,
          color: NEUTRAL.text500,
          fontSize: TYPOGRAPHY.micro.size,
        }}>
          REPLAY CONTEXT
        </div>
        {renderComparisonRow('Window ID', '—', replayCompare.windowId || '—', '→')}
        {renderComparisonRow('Window Start', '—', formatTimestamp(replayCompare.windowStart), '→')}
        {renderComparisonRow('Window End', '—', formatTimestamp(replayCompare.windowEnd), '→')}
        {renderComparisonRow('Deterministic', '—', replayCompare.replayDeterministic ? 'true' : 'false', '→')}
      </div>

      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          padding: SPACING.sp2,
          borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
          fontWeight: 600,
          color: NEUTRAL.text500,
          fontSize: TYPOGRAPHY.micro.size,
        }}>
          EVENT COUNTS
        </div>
        {renderComparisonRow('In Window', '0', replayCompare.totalInWindow.toString(), replayCompare.totalInWindow > 0 ? '↑' : '→')}
        {renderComparisonRow('Out Window', '0', replayCompare.totalOutWindow.toString(), replayCompare.totalOutWindow > 0 ? '↑' : '→')}
        {renderComparisonRow('Total', '0', (replayCompare.totalInWindow + replayCompare.totalOutWindow).toString(), '→')}
      </div>

      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          padding: SPACING.sp2,
          borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
          fontWeight: 600,
          color: NEUTRAL.text500,
          fontSize: TYPOGRAPHY.micro.size,
        }}>
          CONTINUITY STATUS
        </div>
        {renderComparisonRow('Current', '—', continuity.currentStatus, '→')}
      </div>

      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          padding: SPACING.sp2,
          borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
          fontWeight: 600,
          color: NEUTRAL.text500,
          fontSize: TYPOGRAPHY.micro.size,
        }}>
          ADMISSION SUMMARY
        </div>
        {renderComparisonRow('Total', '0', admissibility.totalEntries.toString(), admissibility.totalEntries > 0 ? '↑' : '→')}
        {renderComparisonRow('Passed', '0', admissibility.passedCount.toString(), admissibility.passedCount > 0 ? '↑' : '→')}
        {renderComparisonRow('Refused', '0', admissibility.refusedCount.toString(), admissibility.refusedCount > 0 ? '↓' : '→')}
        {renderComparisonRow('Unknown', '0', admissibility.unknownCount.toString(), admissibility.unknownCount > 0 ? '→' : '→')}
      </div>

      {replayCompare.disclaimer && (
        <div style={{
          padding: SPACING.sp2,
          backgroundColor: NEUTRAL.bg600,
          border: `1px solid ${NEUTRAL.borderSubtle}`,
          borderRadius: 2,
          fontSize: TYPOGRAPHY.micro.size,
          color: NEUTRAL.text400,
        }}>
          <span style={{ fontWeight: 600, color: NEUTRAL.text500 }}>DISCLAIMER: </span>
          {replayCompare.disclaimer}
        </div>
      )}
    </div>
  );
};

export const ReceiptsSurface: React.FC<{ refs: Array<{ refType: 'artifact' | 'receipt'; refKey: string; refHash: string; refPath?: string }> }> = ({ refs }) => {
  const receiptRefs = refs.filter(r => r.refType === 'receipt');
  const artifactRefs = refs.filter(r => r.refType === 'artifact');

  const formatHash = (hash: string) => {
    if (!hash) return '—';
    if (hash.length <= 16) return hash;
    return `${hash.slice(0, 12)}…`;
  };

  if (refs.length === 0) {
    return (
      <div style={{ padding: SPACING.sp3, color: NEUTRAL.text400, fontSize: TYPOGRAPHY.body.size }}>
        No receipts or artifacts available for this event.
      </div>
    );
  }

  return (
    <div style={{ padding: SPACING.sp3 }}>
      {receiptRefs.length > 0 && (
        <div style={{ marginBottom: SPACING.sp4 }}>
          <div style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: NEUTRAL.text500,
            marginBottom: SPACING.sp2,
          }}>
            RECEIPTS
          </div>
          {receiptRefs.map((ref, idx) => (
            <div key={idx} style={{
              padding: SPACING.sp2,
              backgroundColor: NEUTRAL.bg600,
              border: `1px solid ${NEUTRAL.borderSubtle}`,
              borderRadius: 2,
              marginBottom: SPACING.sp2,
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: SPACING.sp1,
              }}>
                <span style={{
                  fontSize: TYPOGRAPHY.caption.size,
                  fontWeight: 600,
                  color: NEUTRAL.text500,
                }}>
                  {ref.refKey || 'RECEIPT'}
                </span>
                <span style={{
                  fontSize: TYPOGRAPHY.micro.size,
                  fontWeight: 600,
                  color: SEMANTIC.stabilized.text,
                  backgroundColor: SEMANTIC.stabilized.chip,
                  padding: '1px 4px',
                  borderRadius: 2,
                  textTransform: 'uppercase',
                  letterSpacing: 0.3,
                }}>
                  VERIFIED
                </span>
              </div>
              <div style={{
                fontSize: TYPOGRAPHY.micro.size,
                color: NEUTRAL.text400,
                fontFamily: TYPOGRAPHY.mono.family,
                marginBottom: SPACING.sp1,
              }}>
                {formatHash(ref.refHash)}
              </div>
              {ref.refPath && (
                <div style={{
                  fontSize: TYPOGRAPHY.micro.size,
                  color: NEUTRAL.text400,
                }}>
                  {ref.refPath}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {artifactRefs.length > 0 && (
        <div>
          <div style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: NEUTRAL.text500,
            marginBottom: SPACING.sp2,
          }}>
            ARTIFACTS
          </div>
          {artifactRefs.map((ref, idx) => (
            <div key={idx} style={{
              padding: SPACING.sp2,
              backgroundColor: NEUTRAL.bg600,
              border: `1px solid ${NEUTRAL.borderSubtle}`,
              borderRadius: 2,
              marginBottom: SPACING.sp2,
            }}>
              <div style={{
                fontSize: TYPOGRAPHY.caption.size,
                fontWeight: 600,
                color: NEUTRAL.text500,
                marginBottom: SPACING.sp1,
              }}>
                {ref.refKey || 'ARTIFACT'}
              </div>
              <div style={{
                fontSize: TYPOGRAPHY.micro.size,
                color: NEUTRAL.text400,
                fontFamily: TYPOGRAPHY.mono.family,
                marginBottom: SPACING.sp1,
              }}>
                {formatHash(ref.refHash)}
              </div>
              {ref.refPath && (
                <div style={{
                  fontSize: TYPOGRAPHY.micro.size,
                  color: NEUTRAL.text400,
                }}>
                  {ref.refPath}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const BottomTabBar: React.FC<{ activeTab: BottomTab; onTabChange: (tab: BottomTab) => void }> = ({ activeTab, onTabChange }) => {
  const tabs: Array<{ id: BottomTab; label: string }> = [
    { id: 'event-log', label: 'EVENT LOG' },
    { id: 'receipts', label: 'RECEIPTS' },
    { id: 'diff-view', label: 'DIFF VIEW' },
    { id: 'replay-comparison', label: 'REPLAY COMPARISON' },
    { id: 'improvement', label: 'IMPROVEMENT' },
    { id: 'audit-summary', label: 'AUDIT SUMMARY' },
    { id: 'continuity-timeline', label: 'CONTINUITY TIMELINE' },
    { id: 'release-state-map', label: 'RELEASE STATE MAP' },
    { id: 'proposal-memory', label: 'PROPOSAL MEMORY' },
    { id: 'verified-surfaces', label: 'VERIFIED SURFACES' },
    { id: 'surface-federation', label: 'SURFACE FEDERATION' },
    { id: 'federated-surfaces', label: 'FEDERATED SURFACES' },
    { id: 'boundary-composition', label: 'BOUNDARY COMPOSITION' },
    { id: 'admission', label: 'ADMISSION' },
    { id: 'evidence', label: 'EVIDENCE' },
    { id: 'receipt', label: 'RECEIPT' },
    { id: 'work-unit', label: 'WORK UNIT' },
    { id: 'next-move', label: 'NEXT MOVE' },
    { id: 'formation-gate', label: 'FORMATION GATE' },
    { id: 'systemic-patterns', label: 'SYSTEMIC PATTERNS' },
    { id: 'constitution', label: 'CONSTITUTION' },
  ];

  return (
    <div style={{
      display: 'flex',
      gap: 0,
      borderBottom: `1px solid ${NEUTRAL.border}`,
      backgroundColor: NEUTRAL.bg600,
    }}>
      {tabs.map((tab) => {
        const isActive = activeTab === tab.id;
        return (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            style={{
              padding: `${SPACING.sp2}px ${SPACING.sp3}px`,
              fontSize: TYPOGRAPHY.micro.size,
              fontWeight: isActive ? 700 : 600,
              border: 'none',
              borderBottom: isActive ? `2px solid ${NEUTRAL.text600}` : '2px solid transparent',
              backgroundColor: isActive ? NEUTRAL.bg200 : 'transparent',
              color: isActive ? NEUTRAL.text600 : NEUTRAL.text400,
              cursor: 'pointer',
              textTransform: 'uppercase',
              letterSpacing: 0.5,
              transition: TRANSITION_TAB_SWITCH,
              position: 'relative',
            }}
            type="button"
            data-testid={`bottom-tab-${tab.id}`}
          >
            {tab.label}
            {isActive && (
              <div style={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: 2,
                backgroundColor: NEUTRAL.text600,
                opacity: 0.5,
              }} />
            )}
          </button>
        );
      })}
    </div>
  );
};

export const GovernanceWorkspaceShell: React.FC<GovernanceWorkspaceShellProps> = ({
  workspace,
  onSelectEvent,
  onToggleSpan,
  onViewModeChange,
  onSelectWorkUnit,
  onSelectEvidenceNode,
  onSelectReceipt,
  onSelectAdmission,
  onSelectGateEntry,
  onSelectPatternEntry,
  onSelectNextMoveEntry,
}) => {
  const panes = workspace.panes;
  const isReplayMode = workspace.viewMode === 'replay';
  const [bottomTab, setBottomTab] = useState<BottomTab>('event-log');

  const handleReplayPositionChange = useCallback((timestamp: string) => {
    // In a real implementation, this would update the replay window position
    // For now, this is a placeholder for the interaction
    console.log('Replay position changed to:', timestamp);
  }, []);

  const handleStepForward = useCallback(() => {
    // Placeholder for step forward action
    console.log('Step forward');
  }, []);

  const handleStepBackward = useCallback(() => {
    // Placeholder for step backward action
    console.log('Step backward');
  }, []);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      backgroundColor: NEUTRAL.bg100,
      overflow: 'hidden',
    }}>
      {/* Toolbar */}
      <WorkspaceToolbar workspace={workspace} onViewModeChange={onViewModeChange} />

      {/* Replay Scrubber - shown only in replay mode */}
      {isReplayMode && (
        <ReplayScrubberPane
          replayModel={panes.replayCompare}
          continuityModel={panes.continuity}
          timelineModel={panes.timeline}
          onPositionChange={handleReplayPositionChange}
          onStepForward={handleStepForward}
          onStepBackward={handleStepBackward}
        />
      )}

      {/* Main content with sidebar */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left sidebar */}
        <WorkspaceSidebar workspace={workspace} />

        {/* Pane grid */}
        <div style={{
          flex: 1,
          display: 'grid',
          gridTemplateColumns: '1.5fr 1fr 380px',
          gridTemplateRows: '1fr 0.6fr',
          gap: 1,
          overflow: 'hidden',
        }}>
        {/* Top-left: Timeline - Primary hero surface */}
        {panes.timeline.visible && (
          <div style={{
            backgroundColor: NEUTRAL.bg100,
            border: `1px solid ${NEUTRAL.border}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gridColumn: '1 / 2',
            gridRow: '1 / 2',
          }}>
            <TimelinePane model={panes.timeline} onSelectEvent={onSelectEvent} />
          </div>
        )}

        {/* Top-center: Trace Explorer or Replay Compare - Secondary */}
        {isReplayMode && panes.replayCompare.visible ? (
          <div style={{
            backgroundColor: NEUTRAL.bg200,
            border: `1px solid ${NEUTRAL.borderSubtle}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gridColumn: '2 / 3',
            gridRow: '1 / 2',
          }}>
            <ReplayComparePane model={panes.replayCompare} onSelectEvent={onSelectEvent} />
          </div>
        ) : panes.traceExplorer.visible ? (
          <div style={{
            backgroundColor: NEUTRAL.bg200,
            border: `1px solid ${NEUTRAL.borderSubtle}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gridColumn: '2 / 3',
            gridRow: '1 / 2',
          }}>
            <TraceExplorerPane
              model={panes.traceExplorer}
              onSelectEvent={onSelectEvent}
              onToggleSpan={onToggleSpan}
            />
          </div>
        ) : null}

        {/* Top-right: Inspector */}
        {panes.inspector.visible && (
          <div style={{
            backgroundColor: NEUTRAL.bg200,
            border: `1px solid ${NEUTRAL.borderSubtle}`,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            gridColumn: '3 / 4',
            gridRow: '1 / 3',
          }}>
            <InspectorPane
              model={panes.inspector}
              replayWindow={isReplayMode && panes.replayCompare.windowId ? {
                start: panes.replayCompare.windowStart || '',
                end: panes.replayCompare.windowEnd || '',
                windowId: panes.replayCompare.windowId
              } : null}
            />
          </div>
        )}

        {/* Bottom-left: Event Log or bottom tab content - Reduced visual competition */}
        <div style={{
          backgroundColor: NEUTRAL.bg200,
          border: `1px solid ${NEUTRAL.borderSubtle}`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          gridColumn: '1 / 3',
          gridRow: '2 / 3',
        }}>
          <BottomTabBar activeTab={bottomTab} onTabChange={setBottomTab} />
          {bottomTab === 'event-log' && panes.eventLog.visible && (
            <EventLogPane model={panes.eventLog} onSelectEvent={onSelectEvent} />
          )}
          {bottomTab === 'receipts' && panes.inspector.detail && (
            <ReceiptsSurface refs={panes.inspector.detail.refs} />
          )}
          {bottomTab === 'receipts' && !panes.inspector.detail && (
            <div style={{ padding: SPACING.sp3, color: NEUTRAL.text400, fontSize: TYPOGRAPHY.body.size }}>
              Select an event to view receipts and artifacts.
            </div>
          )}
          {bottomTab === 'diff-view' && (
            <DiffViewSurface
              replayCompare={{
                windowId: panes.replayCompare.windowId,
                windowStart: panes.replayCompare.windowStart,
                windowEnd: panes.replayCompare.windowEnd,
                totalInWindow: panes.replayCompare.totalInWindow,
                totalOutWindow: panes.replayCompare.totalOutWindow,
                replayDeterministic: panes.replayCompare.replayDeterministic,
                disclaimer: panes.replayCompare.disclaimer,
              }}
              continuity={{
                currentStatus: panes.continuity.currentStatus,
              }}
              admissibility={{
                totalEntries: panes.admissibilityChain.totalEntries,
                refusedCount: panes.admissibilityChain.refusedCount,
                passedCount: panes.admissibilityChain.passedCount,
                unknownCount: panes.admissibilityChain.unknownCount,
              }}
            />
          )}
          {bottomTab === 'replay-comparison' && panes.replayCompare.visible && (
            <ReplayComparePane model={panes.replayCompare} onSelectEvent={onSelectEvent} />
          )}
          {bottomTab === 'improvement' && (
            <ImprovementPane />
          )}
          {bottomTab === 'audit-summary' && (
            <AuditSummaryPane />
          )}
          {bottomTab === 'continuity-timeline' && (
            <ContinuityTimelineSummaryPane />
          )}
          {bottomTab === 'release-state-map' && (
            <ReleaseStateMapPane />
          )}
          {bottomTab === 'proposal-memory' && (
            <ProposalMemoryPane />
          )}
          {bottomTab === 'verified-surfaces' && (
            <VerifiedSurfaceIndexPane />
          )}
          {bottomTab === 'surface-federation' && (
            <SurfaceFederationSummaryPane />
          )}
          {bottomTab === 'federated-surfaces' && (
            <FederatedSurfaceIndexPane />
          )}
          {bottomTab === 'boundary-composition' && (
            <BoundaryCompositionSummaryPane />
          )}
          {bottomTab === 'admission' && panes.admission && (
            <WorkspaceAdmissionPanel model={panes.admission} onSelectWorkUnit={onSelectAdmission ?? onSelectWorkUnit} />
          )}
          {bottomTab === 'admission' && !panes.admission && (
            <div style={{ padding: SPACING.sp3, color: NEUTRAL.text400, fontSize: TYPOGRAPHY.body.size }}>No admission data available.</div>
          )}
          {bottomTab === 'evidence' && panes.evidence && (
            <WorkspaceEvidencePane model={panes.evidence} onSelectNode={onSelectEvidenceNode} />
          )}
          {bottomTab === 'evidence' && !panes.evidence && (
            <div style={{ padding: SPACING.sp3, color: NEUTRAL.text400, fontSize: TYPOGRAPHY.body.size }}>No evidence data available.</div>
          )}
          {bottomTab === 'receipt' && panes.receipt && (
            <WorkspaceReceiptPane model={panes.receipt} onSelectReceipt={onSelectReceipt} />
          )}
          {bottomTab === 'receipt' && !panes.receipt && (
            <div style={{ padding: SPACING.sp3, color: NEUTRAL.text400, fontSize: TYPOGRAPHY.body.size }}>No receipt data available.</div>
          )}
          {bottomTab === 'work-unit' && panes.workUnit && (
            <WorkspaceWorkUnitPane model={panes.workUnit} onSelectWorkUnit={onSelectWorkUnit} />
          )}
          {bottomTab === 'work-unit' && !panes.workUnit && (
            <div style={{ padding: SPACING.sp3, color: NEUTRAL.text400, fontSize: TYPOGRAPHY.body.size }}>No work unit data available.</div>
          )}
          {bottomTab === 'next-move' && panes.nextMove && (
            <WorkspaceNextMovePane model={panes.nextMove} onSelectEntry={onSelectNextMoveEntry} />
          )}
          {bottomTab === 'next-move' && !panes.nextMove && (
            <div style={{ padding: SPACING.sp3, color: NEUTRAL.text400, fontSize: TYPOGRAPHY.body.size }}>No next move data available.</div>
          )}
          {bottomTab === 'formation-gate' && panes.formationGate && (
            <WorkspaceFormationGatePane model={panes.formationGate} onSelectGateEntry={onSelectGateEntry} />
          )}
          {bottomTab === 'formation-gate' && !panes.formationGate && (
            <div style={{ padding: SPACING.sp3, color: NEUTRAL.text400, fontSize: TYPOGRAPHY.body.size }}>No formation gate data available.</div>
          )}
          {bottomTab === 'systemic-patterns' && panes.systemicPatterns && (
            <WorkspaceSystemicPatternsPane model={panes.systemicPatterns} onSelectEntry={onSelectPatternEntry} />
          )}
          {bottomTab === 'systemic-patterns' && !panes.systemicPatterns && (
            <div style={{ padding: SPACING.sp3, color: NEUTRAL.text400, fontSize: TYPOGRAPHY.body.size }}>No systemic pattern data available.</div>
          )}
          {bottomTab === 'constitution' && panes.constitution && (
            <WorkspaceConstitutionPane model={panes.constitution} />
          )}
          {bottomTab === 'constitution' && !panes.constitution && (
            <div style={{ padding: SPACING.sp3, color: NEUTRAL.text400, fontSize: TYPOGRAPHY.body.size }}>No constitution data available.</div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
};
